import { useContext, useEffect, useState } from 'react';
import AnimatedComponent from '../Components/AnimatedComponent';
import Brands from '../Components/Brands';
import { LanguageContext } from '../Components/LanguageContext';
import MobileHeroDiagonal from '../Components/MobileHeroDiagonal';
import NavigationBar from '../Components/NavigationBar';
import NumberInfoBanner from '../Components/NumberInfoBanner';
import ProductRange from '../Components/ProductRange';
import handleTransformLang from '../functions/handleTransformLang';
import Layout from '../Layout';
import infoBannerData from '../lang/corporatePage/infoBanner.json';
import valuableServicesEndData from '../lang/corporatePage/valuableServicesEnd.json';
import valuableServicesStartData from '../lang/corporatePage/valuableServicesStart.json';
import corporatePage from '../lang/pages/corporate.json';
import NavLang from '../lang/pages/nav.json';
import Background from '../public/images/bg_main.svg';

function getIterableData(data) {
  return Object.values(data).map((item) => handleTransformLang(item));
}

function getMobileServiceDescription(description) {
  return description.split('\n')[0];
}

function getDesktopServiceDescription(description) {
  return description.replaceAll('•', '\n•');
}

export default function Corporate() {
  return (
    <Layout headData={corporatePage.head}>
      <InnerPage />
    </Layout>
  );
}

function InnerPage() {
  const { language } = useContext(LanguageContext);
  const [content, setContent] = useState(handleTransformLang(NavLang));
  const [pageContent, setPageContent] = useState(handleTransformLang(corporatePage));
  const [infoBannerItems, setInfoBannerItems] = useState(getIterableData(infoBannerData));
  const [valuableServicesStartItems, setValuableServicesStartItems] = useState(getIterableData(valuableServicesStartData));
  const [valuableServicesEndItems, setValuableServicesEndItems] = useState(getIterableData(valuableServicesEndData));

  useEffect(() => {
    setContent(handleTransformLang(NavLang));
    setPageContent(handleTransformLang(corporatePage));
    setInfoBannerItems(getIterableData(infoBannerData));
    setValuableServicesStartItems(getIterableData(valuableServicesStartData));
    setValuableServicesEndItems(getIterableData(valuableServicesEndData));
  }, [language]);

  const valuableServiceItems = [...valuableServicesStartItems, ...valuableServicesEndItems];
  const primaryStat = infoBannerItems[0];

  return (
    <>
      <section className="relative overflow-hidden bg-white pb-2 md:pb-4 xl:pb-6">
        <div
          className="absolute inset-0 hidden bg-no-repeat sm:block sm:bg-[length:1180px_480px] sm:bg-top 1.5md:bg-[length:1560px_620px] 1.5md:bg-top xl:bg-[length:1760px_700px] xl:bg-top 1.5xl:bg-[length:1915px_750px] 1.5xl:bg-top"
          style={{ backgroundImage: `url(${Background})` }}
        />
        <MobileHeroDiagonal className="top-[11.25rem] h-[16.5rem] w-[12.6rem] sm:top-[5rem]" />
        <div className="h-1.5 w-full bg-bg_secondary" />

        <div className="relative z-20 container mx-auto max-w-8xl px-5 py-5 sm:px-4 sm:py-7 1.5md:py-9 1.5xl:py-11">
          <NavigationBar
            content={content}
            desktopNavBreakpoint="1.5xl"
            logoClassName="h-18 sm:h-20 1.5md:h-22 1.5xl:h-25"
            b2bButtonClassName="px-4 py-[0.45rem] text-[13px] 1.5md:px-4 1.5md:py-[0.45rem] 1.5md:text-[14px] 1.5xl:px-5 1.5xl:py-2 1.5xl:text-lg"
            hamburgerIconClassName="h-9 w-9 sm:h-10 sm:w-10 1.5md:h-11 1.5md:w-11"
          />
        </div>

        <section className="relative z-10 overflow-hidden px-4 pb-0 pt-4 md:pt-8 xl:pt-10">
          <div className="container mx-auto grid max-w-8xl items-center gap-6 1.5md:grid-cols-[0.56fr_1.04fr] md:gap-8 1.5xl:grid-cols-2 1.5xl:gap-16">
            <AnimatedComponent
              direction="animate-fade-left"
              className="order-1 flex justify-center 1.5md:order-2 1.5md:justify-end"
            >
              <img
                src="images/corporate_banner.svg"
                alt=""
                className="relative z-10 mx-auto mt-[1.875rem] w-full max-w-[20rem] object-contain sm:mt-[3.125rem] 1.5md:mt-0 1.5md:max-w-[25rem] 1.5md:translate-y-[74px] xl:max-w-[34rem] xl:translate-x-[8px] xl:translate-y-[96px] 1.5xl:h-125 1.5xl:max-w-none 1.5xl:translate-x-0 1.5xl:translate-y-0 1.5xl:pe-16"
              />
            </AnimatedComponent>

            <AnimatedComponent
              direction="animate-fade-right"
              className="order-2 max-w-[21rem] pt-4 sm:pt-6 1.5md:order-1 1.5md:max-w-[20rem] 1.5md:pt-24 xl:max-w-[28rem] xl:pt-20 1.5xl:max-w-none 1.5xl:pe-4 1.5xl:pt-10"
            >
              <h1 className="text-[23px] font-bold leading-[1.12] text-text_primary sm:text-[25px] 1.5md:text-[28px] xl:text-[32px] 1.5xl:mb-8 1.5xl:text-4xl">
                {pageContent.hero.title}
              </h1>

              <p className="mt-3 text-[14px] leading-[1.55] text-text_secondary 1.5md:mt-4 1.5md:text-[13.5px] xl:mt-5 xl:text-[17px] xl:leading-8 1.5xl:mt-0 1.5xl:text-lg">
                {pageContent.hero.description}
              </p>
            </AnimatedComponent>
          </div>
        </section>
      </section>

      <section className="bg-white pb-14 pt-8 md:pb-18 md:pt-12 xl:pb-22 xl:pt-16">
        <div className="container mx-auto max-w-8xl px-4">
          <div className="space-y-12 md:space-y-14 xl:space-y-18 1.5xl:space-y-52">
            {pageContent.sections?.map((section) => (
              <AnimatedComponent
                key={section.title}
                direction="animate-fade-up"
                className="grid gap-5 md:grid-cols-[11rem_1fr] md:items-center xl:grid-cols-[16rem_1fr] xl:gap-10 1.5xl:grid-cols-[21rem_1fr] 1.5xl:gap-12"
              >
                <div className="flex justify-start md:justify-center">
                  <img
                    src={section.image}
                    alt=""
                    className="h-auto w-[7.125rem] object-contain md:w-[9.75rem] xl:w-[15rem] 1.5xl:h-66 1.5xl:w-84"
                  />
                </div>

                <div>
                  <h2 className="whitespace-pre-line text-[23px] font-bold leading-[1.12] text-text_primary md:text-[28px] xl:text-[32px] 1.5xl:mb-6 1.5xl:text-4xl">
                    {section.title}
                  </h2>
                  <p className="mt-3 max-w-[52rem] text-[14px] leading-6 text-text_secondary md:text-[15px] md:leading-7 xl:text-[16px] xl:leading-8 1.5xl:mt-0 1.5xl:max-w-none 1.5xl:text-lg">
                    {section.description}
                  </p>
                </div>
              </AnimatedComponent>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-bg_secondary">
        <div className="container mx-auto max-w-8xl px-0 md:px-4">
          {primaryStat ? (
            <div className="flex items-center justify-between px-5 py-5 md:hidden">
              <div>
                <div className="text-[3rem] font-bold leading-none text-c_info_banner_text">
                  {primaryStat.title}+
                </div>
                <div className="mt-1 whitespace-pre-line text-[18px] font-bold leading-[1.05] text-white">
                  {primaryStat.description}
                </div>
              </div>
              <img
                src={primaryStat.image}
                alt=""
                className="h-14 w-14 object-contain"
              />
            </div>
          ) : null}

          <div className="hidden md:grid md:grid-cols-4 md:divide-x md:divide-c_info_banner_divider 1.5xl:hidden">
            {infoBannerItems.map((item) => (
              <div key={item.title} className="flex items-center justify-center gap-4 px-5 py-8 xl:px-7 xl:py-9">
                <div>
                  <div className="text-[2.5rem] font-bold leading-none text-c_info_banner_text xl:text-[3rem]">
                    {item.title}+
                  </div>
                  <div className="mt-1 whitespace-pre-line text-[17px] font-bold leading-[1.08] text-white xl:text-[18px]">
                    {item.description}
                  </div>
                </div>
                <img
                  src={item.image}
                  alt=""
                  className="h-12 w-12 object-contain xl:h-14 xl:w-14"
                />
              </div>
            ))}
          </div>

          <div className="hidden 1.5xl:block">
            <NumberInfoBanner infoBannerItems={infoBannerItems} />
          </div>
        </div>
      </section>

      <section id="press" className="bg-bg_primary py-8 md:py-10 xl:py-12">
        <div className="container mx-auto max-w-8xl px-4">
          <h2 className="text-[23px] font-bold leading-tight text-bg_secondary md:text-[28px] xl:text-[32px] 1.5xl:text-4xl">
            {pageContent.pressKit.title}
          </h2>

          <div className="mt-4 grid gap-8 lg:grid-cols-2 lg:gap-12 xl:mt-6 xl:gap-16 1.5xl:gap-25 1.5xl:pb-6 1.5xl:pt-10">
            <div>
              <p className="flex items-start gap-2 text-[14px] leading-6 text-bg_secondary md:text-[15px] 1.5xl:text-lg">
                <span className="mt-[2px] font-bold">✓</span>
                <span>{pageContent.pressKit.logoNote}</span>
              </p>

              <div className="mt-5 flex flex-wrap gap-3 1.5xl:gap-8">
                <a
                  href="downloadables/logo_pdf.zip"
                  className="inline-flex rounded-full bg-white px-5 py-2 text-[14px] font-bold text-text_tertiary transition-shadow duration-200 hover:shadow-md 1.5xl:px-8 1.5xl:text-lg"
                >
                  {pageContent.pressKit.buttons.pdf}
                </a>
                <a
                  href="downloadables/logo_jpeg.zip"
                  className="inline-flex rounded-full bg-white px-5 py-2 text-[14px] font-bold text-text_tertiary transition-shadow duration-200 hover:shadow-md 1.5xl:px-8 1.5xl:text-lg"
                >
                  {pageContent.pressKit.buttons.jpeg}
                </a>
                <a
                  href="downloadables/logo_svg.zip"
                  className="inline-flex rounded-full bg-white px-5 py-2 text-[14px] font-bold text-text_tertiary transition-shadow duration-200 hover:shadow-md 1.5xl:px-8 1.5xl:text-lg"
                >
                  {pageContent.pressKit.buttons.svg}
                </a>
              </div>
            </div>

            <div>
              <p className="flex items-start gap-2 text-[14px] leading-6 text-bg_secondary md:text-[15px] 1.5xl:text-lg">
                <span className="mt-[2px] font-bold">✓</span>
                <span>{pageContent.pressKit.identityNote}</span>
              </p>

              <div className="mt-5">
                <a
                  href="downloadables/KURUMSAL_KIMLIK.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex rounded-full bg-bg_secondary px-6 py-2 text-[14px] font-bold text-white transition-colors duration-200 hover:bg-bg_quaternary 1.5xl:px-8 1.5xl:text-lg"
                >
                  {pageContent.pressKit.buttons.view}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="vision" className="bg-white py-12 md:py-16 xl:py-20">
        <div className="container mx-auto max-w-8xl px-4">
          <div className="grid items-center gap-8 md:grid-cols-[0.95fr_1.05fr] xl:gap-14 1.5xl:grid-cols-2 1.5xl:gap-30">
            <AnimatedComponent direction="animate-fade-right" className="flex justify-center">
              <img
                src="images/vizyon_misyon.svg"
                alt=""
                className="w-full max-w-[22rem] object-contain md:max-w-[27rem] xl:max-w-[31rem] 1.5xl:h-125 1.5xl:max-w-none"
              />
            </AnimatedComponent>

            <AnimatedComponent direction="animate-fade-left">
              <h2 className="text-[23px] font-bold leading-tight text-text_primary md:text-[28px] xl:text-[32px] 1.5xl:mb-6 1.5xl:text-4xl">
                {pageContent.visionMission.visionTitle}
              </h2>
              <p className="mt-3 text-[14px] leading-6 text-text_secondary md:text-[15px] md:leading-7 xl:text-[16px] xl:leading-8 1.5xl:mb-16 1.5xl:mt-0 1.5xl:text-lg">
                {pageContent.visionMission.visionDescription}
              </p>

              <h2 className="mt-7 text-[23px] font-bold leading-tight text-text_primary md:mt-9 md:text-[28px] xl:text-[32px] 1.5xl:mb-6 1.5xl:text-4xl">
                {pageContent.visionMission.missionTitle}
              </h2>
              <p className="mt-3 text-[14px] leading-6 text-text_secondary md:text-[15px] md:leading-7 xl:text-[16px] xl:leading-8 1.5xl:mt-0 1.5xl:text-lg">
                {pageContent.visionMission.missionDescription}
              </p>
            </AnimatedComponent>
          </div>
        </div>
      </section>

      <section className="bg-white pb-14 pt-4 md:pb-18 md:pt-6 xl:pb-20 xl:pt-8">
        <div className="container mx-auto max-w-8xl px-4">
          <h2 className="text-[23px] font-bold leading-tight text-text_primary md:text-[28px] xl:text-[32px] 1.5xl:text-4xl">
            {pageContent.valuableServicesTitle}
          </h2>

          <div className="mt-6 space-y-0 md:hidden">
            {valuableServiceItems.map((item) => (
              <AnimatedComponent
                key={item.title}
                direction="animate-fade-up"
                className="flex gap-4 border-b border-[#dadde5] py-5"
              >
                <img
                  src={item.image}
                  alt=""
                  className="mt-1 h-11 w-11 shrink-0 object-contain"
                />
                <div>
                  <h3 className="text-[18px] font-bold text-text_tertiary">
                    {item.title}
                  </h3>
                  <p className="mt-1 text-[14px] leading-6 text-text_secondary">
                    {getMobileServiceDescription(item.description)}
                  </p>
                </div>
              </AnimatedComponent>
            ))}
          </div>

          <div className="mt-8 hidden md:grid md:grid-cols-2 md:gap-x-12 md:gap-y-8 xl:mt-10 xl:gap-x-16 xl:gap-y-10 1.5xl:hidden">
            {valuableServiceItems.map((item) => (
              <AnimatedComponent
                key={item.title}
                direction="animate-fade-up"
                className="border-b border-[#dadde5] pb-7 xl:pb-8"
              >
                <img
                  src={item.image}
                  alt=""
                  className="h-14 w-auto object-contain xl:h-16"
                />
                <h3 className="mt-4 text-[19px] font-bold text-text_tertiary xl:text-[20px]">
                  {item.title}
                </h3>
                <p className="mt-2 whitespace-pre-line text-[14px] leading-7 text-text_secondary xl:text-[15px]">
                  {getDesktopServiceDescription(item.description)}
                </p>
              </AnimatedComponent>
            ))}
          </div>

          <div className="hidden 1.5xl:grid 1.5xl:grid-cols-2 1.5xl:gap-35 1.5xl:pt-10">
            <div className="grid grid-cols-1 divide-y-2">
              {valuableServicesStartItems.map((item, index) => (
                <AnimatedComponent
                  key={`${item.title}-start`}
                  direction="animate-fade-up"
                  className={`py-12 ${index === 0 ? 'pt-5' : ''}`}
                >
                  <img
                    src={item.image}
                    alt=""
                    className="h-23 object-contain pb-3"
                  />
                  <h3 className="pb-2 text-lg font-bold text-text_tertiary">
                    {item.title}
                  </h3>
                  <p className="whitespace-pre-line text-lg text-text_secondary">
                    {item.description}
                  </p>
                </AnimatedComponent>
              ))}
            </div>

            <div className="grid grid-cols-1 divide-y-2">
              {valuableServicesEndItems.map((item) => (
                <AnimatedComponent
                  key={`${item.title}-end`}
                  direction="animate-fade-up"
                  className="py-5"
                >
                  <img
                    src={item.image}
                    alt=""
                    className="h-23 object-contain pb-3"
                  />
                  <h3 className="pb-2 text-lg font-bold text-text_tertiary">
                    {item.title}
                  </h3>
                  <p className="whitespace-pre-line text-lg text-text_secondary">
                    {item.description}
                  </p>
                </AnimatedComponent>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="brands" className="bg-bg_primary pb-0 pt-12 md:pb-14 md:pt-14 xl:pb-16 xl:pt-16">
        <div className="mx-auto max-w-8xl px-4">
          <div className="pb-8 text-center md:pb-10 xl:pb-12">
            <h2 className="text-[23px] font-bold text-bg_secondary md:text-[28px] xl:text-[32px] 1.5xl:text-4xl">
              {pageContent.productRangeTitle}
            </h2>
          </div>
        </div>

        <ProductRange invert="true" />
      </section>

      <section className="bg-white pb-24 pt-10 md:pb-28 md:pt-12 xl:pb-32 xl:pt-14">
        <div className="mx-auto max-w-8xl px-4">
          <div className="pb-8 text-center md:pb-10 xl:pb-12">
            <h2 className="text-[23px] font-bold text-text_primary md:text-[28px] xl:text-[32px] 1.5xl:text-4xl">
              {pageContent.brandsTitle}
            </h2>
          </div>
        </div>

        <Brands />
      </section>
    </>
  );
}
